import React, { useState, useEffect, useCallback } from 'react';
import { Bot, RefreshCw, Eye } from 'lucide-react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';

// Badges par nature d'outil : lecture (data), action d'interface (ui), écriture (write)
const KIND_STYLE = {
  data:  { label: 'lecture',   cls: 'bg-blue-50 text-blue-700' },
  ui:    { label: 'interface', cls: 'bg-indigo-50 text-indigo-700' },
  write: { label: 'écriture',  cls: 'bg-amber-50 text-amber-700' },
};

/**
 * Debug admin : « ce que l'assistant sait faire » — dump du registre d'outils
 * résolu pour l'org (GET /agent/tools). ?as_user=1 simule la vue d'un
 * utilisateur non-admin (outils masqués par flag/rôle grisés côté back).
 */
export default function AgentToolsDebug({ showNotification }) {
  const [tools, setTools] = useState(null);       // null = pas encore chargé
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [asUser, setAsUser] = useState(false);    // simuler un utilisateur non-admin

  const load = useCallback(async (simulate) => {
    setLoading(true); setUnavailable(false);
    try {
      const res = await fetch(`${BASE}/agent/tools${simulate ? '?as_user=1' : ''}`, {
        headers: { Authorization: `Bearer ${APIService.getToken()}` },
      });
      if (res.status === 404 || res.status === 501) { setUnavailable(true); setTools(null); return; }
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
      const arr = j.tools || j.data?.tools || (Array.isArray(j.data) ? j.data : null) || [];
      setTools(Array.isArray(arr) ? arr : []);
    } catch (e) { setUnavailable(true); setTools(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(asUser); }, [load, asUser]);

  const visibleCount = (tools || []).filter(t => t.visible !== false).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Bot className="w-4 h-4 text-indigo-600" /> Ce que l'assistant sait faire
          {tools && <span className="text-xs font-mono text-gray-400">{visibleCount}/{tools.length} outil(s) visibles</span>}
        </h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={asUser} onChange={e => setAsUser(e.target.checked)} className="w-3.5 h-3.5 accent-indigo-600" />
            <Eye className="w-3.5 h-3.5" /> Vue utilisateur (non-admin)
          </label>
          <button onClick={() => load(asUser)} disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">Registre d'outils résolu pour votre organisation : la liste envoyée au LLM est filtrée par les feature flags et le rôle — l'assistant ne propose jamais une action désactivée.</p>

      {unavailable ? (
        <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-xl px-4 py-6 text-center">
          <code className="font-mono">GET /agent/tools</code> indisponible — endpoint back à déployer.
        </div>
      ) : loading && tools == null ? (
        <div className="text-xs text-gray-400 text-center py-6">Chargement…</div>
      ) : (tools || []).length === 0 ? (
        <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-xl px-4 py-6 text-center">Aucun outil dans le registre.</div>
      ) : (
        <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
          {tools.map((t, i) => {
            const kind = KIND_STYLE[(t.kind || '').toLowerCase()] || { label: t.kind || '—', cls: 'bg-gray-100 text-gray-500' };
            const hidden = t.visible === false;
            return (
              <div key={t.name || i} className={`flex items-start gap-3 px-4 py-2.5 ${hidden ? 'opacity-45' : ''}`}>
                <code className="font-mono text-xs font-semibold text-gray-800 mt-0.5 flex-shrink-0">{t.name}</code>
                <span className={`text-[10.5px] font-semibold rounded px-1.5 py-0.5 flex-shrink-0 ${kind.cls}`}>{kind.label}</span>
                <span className="text-xs text-gray-500 flex-1 min-w-0">{t.description}</span>
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  {t.feature_flag && <span className="text-[10.5px] font-mono bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">flag: {t.feature_flag}</span>}
                  {t.admin_only && <span className="text-[10.5px] font-semibold bg-gray-800 text-white rounded px-1.5 py-0.5">admin</span>}
                  {hidden && <span className="text-[10.5px] font-semibold bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">masqué</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
